import moment from "moment";

function periodTypeFromTenors(tenors) {
  const sample = String(tenors?.[0] || "");
  if (/^Q[1-4]-/i.test(sample)) return "quarter";
  if (/^(Sum|Win)-/i.test(sample)) return "season";
  if (/^Cal-/i.test(sample)) return "calendar";
  return "month";
}

/** Fields the Quick Add API always expects on each row. */
export const QUICK_ADD_REQUIRED_FIELDS = [
  "legs",
  "commodity",
  "contract_style",
  "type",
  "ul",
  "bespoke_tenors",
  "tenor",
  "live",
  "legs_strike",
  "legs_putcall",
  "legs_buysell",
  "legs_ratio",
];

/** Optional — omit when empty so the backend can normalize defaults. */
export const QUICK_ADD_OPTIONAL_FIELDS = [
  "option",
  "buy_qty",
  "buy_percentage",
  "buy",
  "sell",
  "sell_percentage",
  "sell_qty",
  "period_type",
  "average",
  "cost_of_carry",
  "rf",
  "actioned_at_time",
  "black76_fmax",
  "black76_price_steps",
  "black76_time_steps",
];

const hasValue = (v) => v !== "" && v != null;

/** Shorthand premium / vol land on the same Bid / Ask columns as the Pricer. */
function quotesFromParsed(parsed) {
  const prices = Array.isArray(parsed?.prices) ? parsed.prices : [];
  const vols = Array.isArray(parsed?.vols) ? parsed.vols : [];
  const buyAndSell = Boolean(parsed?.sides?.buy && parsed?.sides?.sell);
  const sellOnly = Boolean(parsed?.sides?.sell && !parsed?.sides?.buy);

  if (buyAndSell) {
    return {
      buy: prices[0] ?? "",
      sell: prices[1] ?? "",
      buy_percentage: vols[0] ?? "",
      sell_percentage: vols[1] ?? "",
    };
  }
  if (sellOnly) {
    return {
      buy: "",
      sell: prices[0] ?? "",
      buy_percentage: "",
      sell_percentage: vols[0] ?? "",
    };
  }
  return {
    buy: prices[0] ?? "",
    sell: "",
    buy_percentage: vols[0] ?? "",
    sell_percentage: "",
  };
}

/**
 * Map parseLine result → Trade Capture / Quick Add row keys (TRADE_COLUMNS).
 */
export function toCsvRow(parsed, { optionNumber = 1 } = {}) {
  const legs = parsed?.legs || [];
  const n = legs.length || 1;
  const joinSlash = (vals) => vals.join("/");
  const tenorPerLeg = legs.map((l) => l.tenor).filter(Boolean);
  const tenors =
    tenorPerLeg.length > 0
      ? tenorPerLeg
      : (parsed.tenors || []).length === 1
        ? Array.from({ length: n }, () => parsed.tenors[0])
        : parsed.tenors || [];

  const perLegUl = Array.isArray(parsed.legUnderlyings)
    ? parsed.legUnderlyings.filter((u) => u != null && u !== "")
    : [];
  const ul =
    perLegUl.length === n
      ? perLegUl
      : parsed.underlying != null && parsed.underlying !== ""
        ? Array.from({ length: n }, () => parsed.underlying)
        : [];

  const flags = {
    assumed: [...(parsed.assumed || [])],
  };

  const ratioStr =
    legs.length > 1
      ? legs.map((l) => l.ratio || "1").join("x")
      : legs[0]?.ratio || "1";

  const liveValue =
    parsed.live === 0 || parsed.live === 1 ? String(parsed.live) : "";

  const tenorValue = joinSlash(tenors.length ? tenors : parsed.tenors || []);
  // Comma-joined tenors (e.g. Dec-23,Feb-24) are marked bespoke in imports.
  // Multi-leg E4 slash tenors use N in exported option-row CSVs.
  const bespokeFlag = String(tenorValue).includes(",") ? "Y" : "N";
  const bespokeTenors =
    n > 1 ? Array.from({ length: n }, () => bespokeFlag).join("/") : bespokeFlag;

  return {
    row: {
      option: optionNumber,
      legs: n,
      commodity: parsed.contract?.commodity || "",
      contract_style: parsed.contract?.contract_style || "",
      type: parsed.optionTypeName || parsed.label || "",
      ul: ul.length ? joinSlash(ul) : "",
      bespoke_tenors: bespokeTenors,
      tenor: tenorValue,
      live: liveValue,
      legs_strike: joinSlash(legs.map((l) => l.strike)),
      legs_putcall: joinSlash(legs.map((l) => l.cp)),
      legs_buysell: joinSlash(legs.map((l) => l.bs)),
      legs_ratio: ratioStr,
      buy_qty: "",
      sell_qty: "",
      ...quotesFromParsed(parsed),
      period_type: periodTypeFromTenors(tenors.length ? tenors : parsed.tenors),
      average: "",
      cost_of_carry: "0",
      black76_fmax: "",
      black76_price_steps: "",
      black76_time_steps: "",
      rf: "0",
      actioned_at_time: moment().format("HH:mm:ss"),
      premium_summary: "",
      premium_calculated: "",
    },
    flags,
    parsed,
  };
}

/**
 * Build one Quick Add API row: required fields always present; optional
 * pricing/meta only when non-empty. Display-only columns are never sent.
 */
export function toQuickAddApiRow(row) {
  const out = {};
  QUICK_ADD_REQUIRED_FIELDS.forEach((key) => {
    out[key] = row?.[key] ?? "";
  });
  QUICK_ADD_OPTIONAL_FIELDS.forEach((key) => {
    if (hasValue(row?.[key])) out[key] = row[key];
  });
  return out;
}
